import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { FileText, Clock, CheckCircle2, Inbox, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Tableau de bord"
        subtitle="Vue d'ensemble de votre gestion documentaire"
        index="01"
      />
      <div className="p-6 md:p-10 space-y-8">
        {/* Stat tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 border border-zinc-200" data-testid="stats-tiles">
          <StatTile
            label="Total documents"
            value={stats?.total_documents ?? "—"}
            icon={FileText}
            testid="stat-total"
          />
          <StatTile
            label="À classer"
            value={stats?.pending ?? "—"}
            icon={Clock}
            accent
            testid="stat-pending"
          />
          <StatTile
            label="Classés"
            value={stats?.classified ?? "—"}
            icon={CheckCircle2}
            testid="stat-classified"
            last
          />
        </div>

        {/* Split */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-zinc-200">
          {/* Left: by type */}
          <div className="lg:col-span-1 border-r border-zinc-200 p-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-4">
              Répartition par type
            </div>
            <div className="space-y-3" data-testid="by-type-list">
              {stats?.by_type?.length ? (
                stats.by_type.map((t, i) => (
                  <div key={i} className="flex items-baseline justify-between pb-2 border-b border-zinc-100">
                    <div className="text-sm text-zinc-800 truncate">{t.name}</div>
                    <div className="text-sm font-mono font-semibold">{t.count}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-zinc-500">Aucun document encore.</div>
              )}
            </div>
          </div>

          {/* Right: recent */}
          <div className="lg:col-span-2 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
                Documents récents
              </div>
              <Link
                to="/queue"
                className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1"
                data-testid="dashboard-goto-queue"
              >
                Voir la file <ArrowRight size={12} />
              </Link>
            </div>
            <div className="border border-zinc-200">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/50">
                    <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Nom</th>
                    <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Type</th>
                    <th className="py-3 px-4 text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Statut</th>
                  </tr>
                </thead>
                <tbody data-testid="recent-docs">
                  {stats?.recent_documents?.length ? (
                    stats.recent_documents.map((d) => (
                      <tr key={d.id} className="border-b border-zinc-100 last:border-0">
                        <td className="py-3 px-4 text-sm truncate max-w-[220px]">{d.original_filename}</td>
                        <td className="py-3 px-4 text-sm text-zinc-600">{d.document_type_name || "—"}</td>
                        <td className="py-3 px-4">
                          <StatusBadge status={d.status} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="p-8 text-center text-sm text-zinc-500">
                        Aucun document pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickCard to="/sas" title="Déposer des PDF" subtitle="SAS drag & drop" testid="quick-sas" />
          <QuickCard to="/queue" title="Classer la file" subtitle="Affecter les métadonnées" testid="quick-queue" />
          <QuickCard to="/tree" title="Arborescence" subtitle="Parcourir les classements" testid="quick-tree" />
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, accent, testid, last }) {
  return (
    <div className={`p-6 ${!last ? "md:border-r" : ""} border-zinc-200 bg-white`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{label}</div>
        <Icon size={16} strokeWidth={1.5} className={accent ? "text-[#FF3B30]" : "text-zinc-400"} />
      </div>
      <div className={`mt-4 text-4xl font-semibold tracking-tight tabular-nums ${accent ? "text-[#FF3B30]" : "text-zinc-900"}`}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "À classer", color: "#FF3B30", bg: "#FF3B3010" },
    classified: { label: "Classé", color: "#16A34A", bg: "#16A34A10" },
  };
  const c = map[status] || { label: status, color: "#52525B", bg: "#f4f4f5" };
  return (
    <span
      className="inline-block text-[10px] uppercase tracking-wider font-mono font-medium px-2 py-1"
      style={{ color: c.color, backgroundColor: c.bg, border: `1px solid ${c.color}40` }}
    >
      {c.label}
    </span>
  );
}

function QuickCard({ to, title, subtitle, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group border border-zinc-200 p-6 bg-white hover:bg-zinc-50 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{subtitle}</div>
          <div className="mt-2 text-lg font-semibold text-zinc-900">{title}</div>
        </div>
        <ArrowRight size={18} strokeWidth={1.5} className="text-zinc-400 group-hover:text-[#002FA7] group-hover:translate-x-1 transition-all" />
      </div>
    </Link>
  );
}
